import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Card, CardContent } from '../ui/card';
import { Globe, Rocket, Bot, Briefcase, TrendingUp, Trophy } from 'lucide-react';

const Impact = () => {
  const { t } = useTranslation();

  const impactMetrics = [
    {
      id: 1,
      value: "95%",
      label: t('vision30.impact.internetPenetration'),
      description: t('vision30.impact.internetPenetrationDesc'),
      icon: Globe
    },
    {
      id: 2,
      value: "500+",
      label: t('vision30.impact.startups'),
      description: t('vision30.impact.startupsDesc'),
      icon: Rocket
    },
    {
      id: 3,
      value: "80%",
      label: t('vision30.impact.aiAdoption'),
      description: t('vision30.impact.aiAdoptionDesc'),
      icon: Bot
    },
    {
      id: 4,
      value: "2M+",
      label: t('vision30.impact.digitalJobs'),
      description: t('vision30.impact.digitalJobsDesc'),
      icon: Briefcase
    },
    {
      id: 5,
      value: "15%",
      label: t('vision30.impact.gdpContribution'),
      description: t('vision30.impact.gdpContributionDesc'),
      icon: TrendingUp
    },
    {
      id: 6,
      value: "#1",
      label: t('vision30.impact.africaRanking'),
      description: t('vision30.impact.africaRankingDesc'),
      icon: Trophy
    }
  ];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('vision30.impact.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('vision30.impact.subtitle')}
          </p>
        </div>

        {/* Impact Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {impactMetrics.map((metric, index) => (
            <Card 
              key={metric.id} 
              className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 bg-gradient-to-br from-background to-muted/20"
            >
              <CardContent className="p-8 text-center">
                {/* Icon */}
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {React.createElement(metric.icon, { className: "h-12 w-12 text-primary" })}
                </div>

                {/* Value */}
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2 group-hover:text-accent transition-colors duration-300">
                  {metric.value}
                </div>

                {/* Label */}
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {metric.label}
                </h3>

                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {metric.description}
                </p>

                {/* Animated Progress Bar */}
                <div className="mt-6">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-2000 ease-out"
                      style={{ 
                        width: `${Math.min(60 + (index * 8), 100)}%`,
                        animationDelay: `${index * 200}ms`
                      }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Vision Statement */}
        <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {t('vision30.impact.visionTitle')}
          </h3>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
            {t('vision30.impact.visionDescription')}
          </p>
        </div>

        {/* Key Achievements */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-foreground text-center mb-12">
            {t('vision30.impact.achievementsTitle')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement1.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement1.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement2.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement2.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement3.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement3.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">4</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement4.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement4.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">5</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement5.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement5.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">6</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.impact.achievement6.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.impact.achievement6.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Impact;
